'use client'

import { useMemo, useState } from 'react'
import { cn } from '@nwwp/ui-shadcn'
import { projectSavingsByAge } from './calculator'
import { HowItWorks } from './HowItWorks'
import { InputsPanel } from './InputsPanel'
import { MobileFloatingBar } from './MobileFloatingBar'
import { LockedPreview, OnTrackUnlockForm } from './ResultsGate'
import { ResultsSummary } from './ResultsSummary'
import { SavingsChart } from './SavingsChart'
import type { CalculatorState } from './types'
import { useOnTrackUnlocked } from './unlock-state'
import { useCalculator } from './use-calculator'
import type { CreateLeadMagnetLeadFn } from '../../../lead-magnets/components/LeadCaptureForm'

export interface OnTrackCalculatorProps {
  createLead: CreateLeadMagnetLeadFn
  initialState?: Partial<CalculatorState>
  /** Storybook only — the page relies on the unlock cookie. */
  initialUnlocked?: boolean
}

export function OnTrackCalculator({ createLead, initialState, initialUnlocked = false }: OnTrackCalculatorProps) {
  const { state, setters, results } = useCalculator(initialState)
  const [showFloatBar, setShowFloatBar] = useState(true)
  const unlocked = useOnTrackUnlocked() || initialUnlocked
  const savingsByAge = useMemo(() => projectSavingsByAge(state), [state])

  return (
    <div className="mx-auto grid max-w-300 grid-cols-1 items-start gap-6 px-4 tablet:grid-cols-12 tablet:gap-8 tablet:px-8">
      <div className={cn('tablet:col-span-5', !unlocked && 'order-2 tablet:order-1')}>
        <InputsPanel state={state} setters={setters} results={results}>
          {!unlocked && <OnTrackUnlockForm createLead={createLead} state={state} />}
        </InputsPanel>
      </div>

      <div
        className={cn(
          'flex flex-col gap-6 tablet:col-span-7',
          !unlocked && 'order-1 tablet:sticky tablet:top-6 tablet:order-2 tablet:self-start'
        )}
      >
        {unlocked ? (
          <>
            <ResultsSummary results={results} />
            <SavingsChart
              points={savingsByAge}
              whatYouNeed={results.whatYouNeed}
              retirementAge={savingsByAge[savingsByAge.length - 1]?.age ?? state.retirementAge}
            />
            <HowItWorks results={results} />
          </>
        ) : (
          <LockedPreview state={state} />
        )}
      </div>

      {unlocked && showFloatBar && <MobileFloatingBar onClose={() => setShowFloatBar(false)} />}
    </div>
  )
}
